import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Button,
  Empty,
  Form,
  Input,
  Modal,
  Space,
  Table,
  Tag,
  TimePicker,
  message,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  ClockCircleOutlined,
  FieldTimeOutlined,
  PlusOutlined,
  ReloadOutlined,
  StopOutlined,
  UserOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import moment from 'moment-jalaali';
import { useLocation } from 'react-router-dom';
import MainLayout from '../../../layouts/MainLayout';
import PersianDatePicker from '../../../components/PersianDatePicker';
import { TableSkeleton } from '../../../components/SkeletonLoaders';
import {
  overtimeRequestsApi,
  show422Error,
  ValidationError,
} from '../../../utils/api';
import { toPersianDigits } from '../../../utils/formatPriceToman';
import { toJalali } from '../../../utils/dateUtils';

const { TextArea } = Input;

const STATUS_META = {
  pending_manager: { color: 'processing', label: 'در انتظار مدیر' },
  pending_hr: { color: 'warning', label: 'در انتظار منابع انسانی' },
  approved: { color: 'success', label: 'تأیید نهایی شده' },
  rejected: { color: 'error', label: 'رد شده' },
  canceled: { color: 'default', label: 'لغو شده' },
};

const TABS = [
  { key: 'my_requests', label: 'درخواست‌های من', countKey: 'my_pending' },
  { key: 'manager_pending', label: 'کارتابل مدیر', countKey: 'manager_pending' },
  { key: 'hr_pending', label: 'کارتابل منابع انسانی', countKey: 'hr_pending' },
  { key: 'approved', label: 'تأییدشده‌ها', countKey: 'total_approved' },
];

const formatDuration = (value) => {
  if (value == null || value === '') return '—';
  const s = String(value).trim();
  if (/^\d{1,2}:\d{2}/.test(s)) return toPersianDigits(s.slice(0, 5));
  const isoMatch = s.match(/T(\d{2}:\d{2})/);
  if (isoMatch) return toPersianDigits(isoMatch[1]);
  const spaceMatch = s.match(/\s(\d{2}:\d{2})/);
  if (spaceMatch) return toPersianDigits(spaceMatch[1]);
  return toPersianDigits(s);
};

const formatDisplayDate = (row) => {
  const rawJalali = row?.jalali_date || row?.date_jalali || row?.jdate;
  if (rawJalali) {
    const asText = String(rawJalali).trim();
    const latinized = asText
      .replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
      .replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))
      .replace(/-/g, '/')
      .slice(0, 10);
    const year = Number(latinized.split('/')[0]);
    if (year >= 1300 && year <= 1600 && /^\d{4}\/\d{2}\/\d{2}$/.test(latinized)) {
      return toPersianDigits(latinized);
    }
    const fromJalali = toJalali(asText);
    if (fromJalali) return toPersianDigits(fromJalali);
  }
  const gregorian = row?.date;
  if (gregorian) {
    const converted = toJalali(String(gregorian).slice(0, 10));
    if (converted) return toPersianDigits(converted);
  }
  return '—';
};

/** نمایش ساعت درخواست / واقعی / مصوب */
const TimeTriple = ({ requested, actual, approved }) => {
  const tones = {
    requested:
      'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800/60 dark:text-slate-200 dark:border-slate-700',
    actual:
      'bg-violet-50 text-violet-800 border-violet-200 dark:bg-violet-950/40 dark:text-violet-200 dark:border-violet-800',
    approved:
      'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-200 dark:border-emerald-800',
  };
  const Chip = ({ label, value, tone }) => (
    <div
      className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-[11px] leading-none ${tones[tone]}`}
      style={{ fontFamily: 'CustomFont' }}
    >
      <span className="opacity-70 whitespace-nowrap">{label}</span>
      <span className="font-semibold tabular-nums tracking-wide">{formatDuration(value)}</span>
    </div>
  );
  return (
    <div className="flex flex-col gap-1.5 items-start">
      <Chip label="درخواست" value={requested} tone="requested" />
      <Chip label="واقعی" value={actual} tone="actual" />
      <Chip label="مصوب" value={approved} tone="approved" />
    </div>
  );
};

const jalaliToGregorian = (jalali) => {
  if (!jalali) return '';
  const normalized = String(jalali)
    .replace(/[۰-۹]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d))
    .replace(/[٠-٩]/g, (d) => '٠١٢٣٤٥٦٧٨٩'.indexOf(d))
    .replace(/-/g, '/');
  const m = moment(normalized, 'jYYYY/jMM/jDD');
  return m.isValid() ? m.format('YYYY-MM-DD') : '';
};

const OvertimeRequests = () => {
  const location = useLocation();
  const navState = location.state || {};
  const [tab, setTab] = useState(navState.tab || 'my_requests');
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({
    my_pending: 0,
    manager_pending: 0,
    hr_pending: 0,
    total_approved: 0,
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [total, setTotal] = useState(0);

  const [createOpen, setCreateOpen] = useState(Boolean(navState.openCreate));
  const [createLoading, setCreateLoading] = useState(false);
  const [actualPreview, setActualPreview] = useState(null);
  const [actualLoading, setActualLoading] = useState(false);

  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [managerOpen, setManagerOpen] = useState(false);
  const [managerTarget, setManagerTarget] = useState(null);
  const [hrOpen, setHrOpen] = useState(false);
  const [hrTarget, setHrTarget] = useState(null);

  const [form] = Form.useForm();
  const [rejectForm] = Form.useForm();
  const [managerForm] = Form.useForm();
  const [hrForm] = Form.useForm();

  const loadCounts = useCallback(async () => {
    try {
      const res = await overtimeRequestsApi.getCounts();
      const data = res?.data || res || {};
      setCounts({
        my_pending: data.my_pending || 0,
        manager_pending: data.manager_pending || 0,
        hr_pending: data.hr_pending || 0,
        total_approved: data.total_approved || 0,
      });
    } catch (e) {
      console.warn('overtime counts:', e?.message || e);
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await overtimeRequestsApi.getAll({
        tab,
        page,
        per_page: pageSize,
      });
      const data = res?.data || res || {};
      const list = Array.isArray(data.requests)
        ? data.requests
        : Array.isArray(data.data)
          ? data.data
          : Array.isArray(data)
            ? data
            : [];
      setRows(list);
      if (data.counts) {
        setCounts((prev) => ({ ...prev, ...data.counts }));
      }
      setTotal(data.pagination?.total ?? data.meta?.total ?? list.length);
    } catch (error) {
      console.error(error);
      message.error(error?.message || 'خطا در دریافت درخواست‌های اضافه‌کاری');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [tab, page, pageSize]);

  useEffect(() => {
    loadCounts();
  }, [loadCounts]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const fetchActualForDate = async (jalaliDate) => {
    const gregorian = jalaliToGregorian(jalaliDate);
    if (!gregorian) {
      setActualPreview(null);
      return;
    }
    setActualLoading(true);
    try {
      const res = await overtimeRequestsApi.calculateActual(gregorian);
      const payload = res?.data ?? res ?? {};
      const nested =
        payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data)
          ? payload.data
          : payload;
      setActualPreview(nested);
      // پیشنهاد مقدار مصوب اولیه در فرم ایجاد: همان واقعی یا درخواستی
      if (nested?.actual_time_formatted && !form.getFieldValue('requested_time')) {
        form.setFieldsValue({
          requested_time: dayjs(String(nested.actual_time_formatted).slice(0, 5), 'HH:mm'),
        });
      }
    } catch (err) {
      console.warn('calculate-actual:', err?.message || err);
      setActualPreview(null);
    } finally {
      setActualLoading(false);
    }
  };

  useEffect(() => {
    if (!createOpen) {
      setActualPreview(null);
      return;
    }
    form.setFieldsValue({
      date_jalali: '',
      requested_time: dayjs('03:00', 'HH:mm'),
      start_time: dayjs('17:00', 'HH:mm'),
      end_time: dayjs('20:00', 'HH:mm'),
      reason: '',
    });
  }, [createOpen, form]);

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      const dateGregorian = jalaliToGregorian(values.date_jalali);
      if (!dateGregorian) {
        message.error('تاریخ معتبر وارد کنید');
        return;
      }
      if (!values.requested_time) {
        message.error('میزان ساعت درخواستی الزامی است');
        return;
      }
      setCreateLoading(true);
      const payload = {
        date: dateGregorian,
        requested_time: values.requested_time.format('HH:mm'),
        reason: values.reason,
      };
      if (values.start_time) payload.start_time = values.start_time.format('HH:mm');
      if (values.end_time) payload.end_time = values.end_time.format('HH:mm');

      await overtimeRequestsApi.create(payload);
      message.success('درخواست اضافه‌کاری ثبت و برای مدیر ارسال شد');
      setCreateOpen(false);
      form.resetFields();
      setActualPreview(null);
      loadCounts();
      if (tab !== 'my_requests') setTab('my_requests');
      else loadList();
    } catch (error) {
      if (error?.errorFields) return;
      if (error instanceof ValidationError || error?.error?.details) {
        show422Error(error, form);
      } else {
        message.error(error?.message || 'خطا در ثبت درخواست');
      }
    } finally {
      setCreateLoading(false);
    }
  };

  const runAction = async (id, fn, successMsg) => {
    setActionLoadingId(id);
    try {
      await fn();
      message.success(successMsg);
      loadCounts();
      loadList();
    } catch (error) {
      message.error(error?.message || 'خطا در انجام عملیات');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleManagerApprove = async () => {
    try {
      const values = await managerForm.validateFields();
      if (!managerTarget?.id) return;
      await runAction(
        managerTarget.id,
        () =>
          overtimeRequestsApi.managerApprove(managerTarget.id, {
            comment: values.comment || undefined,
          }),
        'تأیید مدیر ثبت شد',
      );
      setManagerOpen(false);
      setManagerTarget(null);
      managerForm.resetFields();
    } catch (error) {
      if (error?.errorFields) return;
    }
  };

  const handleHrApprove = async () => {
    try {
      const values = await hrForm.validateFields();
      if (!hrTarget?.id) return;
      const approvedHours = values.approved_hours
        ? values.approved_hours.format('HH:mm')
        : null;
      if (!approvedHours) {
        message.error('ساعت مصوب الزامی است');
        return;
      }
      await runAction(
        hrTarget.id,
        () =>
          overtimeRequestsApi.hrApprove(hrTarget.id, {
            approved_hours: approvedHours,
            comment: values.comment || undefined,
          }),
        'اضافه‌کاری تأیید نهایی شد',
      );
      setHrOpen(false);
      setHrTarget(null);
      hrForm.resetFields();
    } catch (error) {
      if (error?.errorFields) return;
    }
  };

  const handleReject = async () => {
    try {
      const values = await rejectForm.validateFields();
      if (!rejectTarget?.id) return;
      await runAction(
        rejectTarget.id,
        () =>
          overtimeRequestsApi.reject(rejectTarget.id, {
            rejection_reason: values.rejection_reason,
          }),
        'درخواست رد شد',
      );
      setRejectOpen(false);
      setRejectTarget(null);
      rejectForm.resetFields();
    } catch (error) {
      if (error?.errorFields) return;
    }
  };

  const openHrApprove = (row) => {
    setHrTarget(row);
    const suggest =
      row.actual_time_formatted ||
      row.requested_time_formatted ||
      '02:30';
    hrForm.setFieldsValue({
      approved_hours: dayjs(String(suggest).slice(0, 5), 'HH:mm'),
      comment: '',
    });
    setHrOpen(true);
  };

  const columns = useMemo(
    () => [
      {
        title: <span style={{ fontFamily: 'CustomFont' }}>کارمند</span>,
        key: 'user',
        width: 190,
        render: (_, row) => (
          <div className="flex items-center gap-2" style={{ fontFamily: 'CustomFont' }}>
            <Avatar size={32} src={row.user?.image} icon={<UserOutlined />} />
            <div>
              <div className="font-medium">{row.user?.name || '—'}</div>
              <div className="text-xs text-gray-500">
                {row.user?.employee_detail?.department?.team_name || '—'}
              </div>
            </div>
          </div>
        ),
      },
      {
        title: <span style={{ fontFamily: 'CustomFont' }}>تاریخ</span>,
        key: 'date',
        width: 110,
        render: (_, row) => (
          <span
            className="tabular-nums text-sm font-medium text-slate-800 dark:text-slate-100"
            style={{ fontFamily: 'CustomFont' }}
            dir="ltr"
          >
            {formatDisplayDate(row)}
          </span>
        ),
      },
      {
        title: <span style={{ fontFamily: 'CustomFont' }}>ساعت‌ها</span>,
        key: 'triple',
        width: 200,
        render: (_, row) => (
          <TimeTriple
            requested={row.requested_time_formatted}
            actual={row.actual_time_formatted}
            approved={row.approved_time_formatted}
          />
        ),
      },
      {
        title: <span style={{ fontFamily: 'CustomFont' }}>بازه / دلیل</span>,
        key: 'reason',
        render: (_, row) => (
          <div style={{ fontFamily: 'CustomFont' }} className="text-sm space-y-0.5">
            {row.start_time || row.end_time ? (
              <div className="text-xs text-slate-500 tabular-nums" dir="ltr">
                {formatDuration(row.start_time)} – {formatDuration(row.end_time)}
              </div>
            ) : null}
            <div>{row.reason || '—'}</div>
            {row.rejection_reason ? (
              <div className="text-xs text-red-600">رد: {row.rejection_reason}</div>
            ) : null}
          </div>
        ),
      },
      {
        title: <span style={{ fontFamily: 'CustomFont' }}>وضعیت</span>,
        key: 'status',
        width: 140,
        render: (_, row) => {
          const meta = STATUS_META[row.status] || {
            color: 'default',
            label: row.status_label || row.status,
          };
          return (
            <Tag color={meta.color} className="m-0" style={{ fontFamily: 'CustomFont' }}>
              {row.status_label || meta.label}
            </Tag>
          );
        },
      },
      {
        title: <span style={{ fontFamily: 'CustomFont' }}>عملیات</span>,
        key: 'actions',
        width: 230,
        render: (_, row) => {
          const busy = actionLoadingId === row.id;
          return (
            <Space wrap size={[4, 4]}>
              {tab === 'manager_pending' && row.status === 'pending_manager' ? (
                <>
                  <Button
                    size="small"
                    type="primary"
                    icon={<CheckOutlined />}
                    loading={busy}
                    style={{ fontFamily: 'CustomFont' }}
                    onClick={() => {
                      setManagerTarget(row);
                      managerForm.setFieldsValue({ comment: '' });
                      setManagerOpen(true);
                    }}
                  >
                    تأیید مدیر
                  </Button>
                  <Button
                    size="small"
                    danger
                    icon={<CloseOutlined />}
                    loading={busy}
                    style={{ fontFamily: 'CustomFont' }}
                    onClick={() => {
                      setRejectTarget(row);
                      setRejectOpen(true);
                    }}
                  >
                    رد
                  </Button>
                </>
              ) : null}
              {tab === 'hr_pending' && row.status === 'pending_hr' ? (
                <>
                  <Button
                    size="small"
                    type="primary"
                    icon={<CheckOutlined />}
                    loading={busy}
                    className="bg-emerald-600 hover:bg-emerald-700 border-emerald-600"
                    style={{ fontFamily: 'CustomFont' }}
                    onClick={() => openHrApprove(row)}
                  >
                    تأیید نهایی
                  </Button>
                  <Button
                    size="small"
                    danger
                    icon={<CloseOutlined />}
                    loading={busy}
                    style={{ fontFamily: 'CustomFont' }}
                    onClick={() => {
                      setRejectTarget(row);
                      setRejectOpen(true);
                    }}
                  >
                    رد
                  </Button>
                </>
              ) : null}
              {tab === 'my_requests' && row.status === 'pending_manager' ? (
                <Button
                  size="small"
                  icon={<StopOutlined />}
                  loading={busy}
                  style={{ fontFamily: 'CustomFont' }}
                  onClick={() =>
                    runAction(
                      row.id,
                      () => overtimeRequestsApi.cancel(row.id),
                      'درخواست لغو شد',
                    )
                  }
                >
                  لغو
                </Button>
              ) : null}
            </Space>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tab, actionLoadingId],
  );

  return (
    <MainLayout>
      <div style={{ fontFamily: 'CustomFont' }}>
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div>
            <h2 className="text-lg font-semibold m-0 flex items-center gap-2">
              <FieldTimeOutlined className="text-amber-600" />
              مدیریت اضافه‌کاری
            </h2>
          </div>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                loadCounts();
                loadList();
              }}
              loading={loading}
            >
              بروزرسانی
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
              درخواست اضافه‌کاری
            </Button>
          </Space>
        </div>

        <div className="flex flex-wrap items-center gap-1.5 mb-4">
          {TABS.map((t) => {
            const count = counts[t.countKey] || 0;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => {
                  setTab(t.key);
                  setPage(1);
                }}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs border transition-all leading-none"
                style={{
                  fontFamily: 'CustomFont',
                  background: active ? '#b45309' : '#fff',
                  color: active ? '#fff' : '#475569',
                  borderColor: active ? '#b45309' : '#e2e8f0',
                  fontWeight: active ? 600 : 500,
                }}
              >
                {t.label}
                {count > 0 ? (
                  <span
                    className="inline-flex items-center justify-center min-w-[1.1rem] h-4 px-1 rounded-full text-[10px] font-semibold tabular-nums"
                    style={{
                      backgroundColor: active ? 'rgba(255,255,255,0.22)' : '#fef3c7',
                      color: active ? '#fff' : '#b45309',
                    }}
                  >
                    {toPersianDigits(count)}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>

        {loading && !rows.length ? (
          <TableSkeleton rows={5} columns={6} />
        ) : (
          <Table
            rowKey="id"
            columns={columns}
            dataSource={rows}
            loading={loading}
            scroll={{ x: 980 }}
            locale={{
              emptyText: (
                <Empty
                  description={
                    <span style={{ fontFamily: 'CustomFont' }}>درخواستی یافت نشد</span>
                  }
                />
              ),
            }}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
              onChange: (p, size) => {
                setPage(p);
                setPageSize(size);
              },
            }}
          />
        )}

        {/* ثبت درخواست */}
        <Modal
          title={
            <span style={{ fontFamily: 'CustomFont' }} className="inline-flex items-center gap-2">
              <ClockCircleOutlined />
              ثبت درخواست اضافه‌کاری
            </span>
          }
          open={createOpen}
          onCancel={() => {
            setCreateOpen(false);
            setActualPreview(null);
          }}
          onOk={handleCreate}
          confirmLoading={createLoading}
          okText="ارسال برای مدیر"
          cancelText="انصراف"
          destroyOnClose
          width={520}
        >
          <Form form={form} layout="vertical" className="mt-2" style={{ fontFamily: 'CustomFont' }}>
            <Form.Item
              name="date_jalali"
              label="تاریخ"
              rules={[{ required: true, message: 'تاریخ الزامی است' }]}
            >
              <PersianDatePicker
                placeholder="انتخاب تاریخ"
                style={{ width: '100%' }}
                onChange={(val) => {
                  form.setFieldsValue({ date_jalali: val });
                  fetchActualForDate(val);
                }}
              />
            </Form.Item>

            {actualPreview || actualLoading ? (
              <Alert
                className="mb-3 custom-font text-[12px]"
                type="info"
                showIcon
                message={
                  actualLoading
                    ? 'در حال محاسبه ساعت واقعی…'
                    : `ساعت واقعی بر اساس تردد: ${formatDuration(
                        actualPreview?.actual_time_formatted,
                      )}`
                }
                description={
                  actualPreview && !actualLoading
                    ? `ورود ${formatDuration(
                        actualPreview.clock_in_time,
                      )} · خروج ${formatDuration(actualPreview.clock_out_time)}`
                    : null
                }
              />
            ) : null}

            <div className="grid grid-cols-3 gap-3">
              <Form.Item
                name="requested_time"
                label="ساعت درخواستی"
                rules={[{ required: true, message: 'الزامی' }]}
              >
                <TimePicker format="HH:mm" style={{ width: '100%' }} placeholder="03:00" />
              </Form.Item>
              <Form.Item name="start_time" label="از ساعت">
                <TimePicker format="HH:mm" style={{ width: '100%' }} placeholder="17:00" />
              </Form.Item>
              <Form.Item name="end_time" label="تا ساعت">
                <TimePicker format="HH:mm" style={{ width: '100%' }} placeholder="20:00" />
              </Form.Item>
            </div>

            <Form.Item
              name="reason"
              label="دلیل"
              rules={[{ required: true, message: 'دلیل الزامی است' }]}
            >
              <TextArea rows={3} placeholder="دلیل درخواست را بنویسید" />
            </Form.Item>
          </Form>
        </Modal>

        {/* تأیید مدیر */}
        <Modal
          title={<span style={{ fontFamily: 'CustomFont' }}>تأیید مدیر</span>}
          open={managerOpen}
          onCancel={() => {
            setManagerOpen(false);
            setManagerTarget(null);
          }}
          onOk={handleManagerApprove}
          confirmLoading={actionLoadingId === managerTarget?.id}
          okText="تأیید"
          cancelText="انصراف"
          destroyOnClose
        >
          {managerTarget ? (
            <div className="mb-3" style={{ fontFamily: 'CustomFont' }}>
              <TimeTriple
                requested={managerTarget.requested_time_formatted}
                actual={managerTarget.actual_time_formatted}
                approved={managerTarget.approved_time_formatted}
              />
            </div>
          ) : null}
          <Form form={managerForm} layout="vertical" style={{ fontFamily: 'CustomFont' }}>
            <Form.Item name="comment" label="یادداشت">
              <TextArea rows={3} placeholder="در صورت نیاز یادداشت بنویسید" />
            </Form.Item>
          </Form>
        </Modal>

        {/* تأیید منابع انسانی */}
        <Modal
          title={<span style={{ fontFamily: 'CustomFont' }}>تأیید نهایی و تعیین ساعت مصوب</span>}
          open={hrOpen}
          onCancel={() => {
            setHrOpen(false);
            setHrTarget(null);
          }}
          onOk={handleHrApprove}
          confirmLoading={actionLoadingId === hrTarget?.id}
          okText="تأیید نهایی"
          cancelText="انصراف"
          destroyOnClose
          width={480}
        >
          {hrTarget ? (
            <div className="mb-3" style={{ fontFamily: 'CustomFont' }}>
              <TimeTriple
                requested={hrTarget.requested_time_formatted}
                actual={hrTarget.actual_time_formatted}
                approved={hrTarget.approved_time_formatted}
              />
            </div>
          ) : null}
          <Form form={hrForm} layout="vertical" style={{ fontFamily: 'CustomFont' }}>
            <Form.Item
              name="approved_hours"
              label="ساعت مصوب"
              rules={[{ required: true, message: 'ساعت مصوب الزامی است' }]}
            >
              <TimePicker format="HH:mm" style={{ width: '100%' }} placeholder="02:30" />
            </Form.Item>
            <Form.Item name="comment" label="یادداشت">
              <TextArea rows={2} placeholder="در صورت نیاز یادداشت بنویسید" />
            </Form.Item>
          </Form>
        </Modal>

        {/* رد */}
        <Modal
          title={<span style={{ fontFamily: 'CustomFont' }}>رد درخواست اضافه‌کاری</span>}
          open={rejectOpen}
          onCancel={() => {
            setRejectOpen(false);
            setRejectTarget(null);
          }}
          onOk={handleReject}
          confirmLoading={actionLoadingId === rejectTarget?.id}
          okText="رد درخواست"
          okButtonProps={{ danger: true }}
          cancelText="انصراف"
          destroyOnClose
        >
          <Form form={rejectForm} layout="vertical" style={{ fontFamily: 'CustomFont' }}>
            <Form.Item
              name="rejection_reason"
              label="علت رد"
              rules={[{ required: true, message: 'علت رد الزامی است' }]}
            >
              <TextArea rows={3} placeholder="علت رد را بنویسید" />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default OvertimeRequests;
